import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Tone = "danger" | "brand";

const TONE_STYLES: Record<Tone, { iconWrap: string; icon: string; confirm: string }> = {
  danger: {
    iconWrap: "bg-gradient-to-br from-accent-red/25 to-accent-red/5 ring-1 ring-accent-red/30",
    icon: "text-accent-red",
    confirm: cn(
      "bg-gradient-to-br from-red-400 via-accent-red to-red-700",
      "shadow-[0_10px_28px_-8px_rgba(239,68,68,0.65),inset_0_1px_0_0_rgba(255,255,255,0.18)] ring-1 ring-accent-red/50",
      "hover:shadow-[0_16px_36px_-8px_rgba(239,68,68,0.9)]"
    ),
  },
  brand: {
    iconWrap: "bg-gradient-to-br from-brand-400/30 to-brand-600/10 ring-1 ring-brand-500/30",
    icon: "text-brand-200",
    confirm: cn(
      "bg-gradient-to-br from-brand-300 via-brand-500 to-brand-700",
      "shadow-[0_10px_28px_-8px_rgba(59,130,246,0.65),inset_0_1px_0_0_rgba(255,255,255,0.18)] ring-1 ring-brand-400/50",
      "hover:shadow-[0_16px_36px_-8px_rgba(59,130,246,0.9)]"
    ),
  },
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  icon: Icon = AlertTriangle,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  icon?: LucideIcon;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const styles = TONE_STYLES[tone];

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/65 backdrop-blur-md"
        onClick={onClose}
      />

      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-800/95 to-ink-900/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl"
      >
        <div
          className={cn(
            "pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full blur-3xl",
            tone === "danger" ? "bg-accent-red/15" : "bg-brand-500/20"
          )}
        />

        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-lg border border-white/[0.06] bg-white/[0.02] text-slate-400 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative px-6 pb-6 pt-7">
          <div className={cn("grid h-12 w-12 place-items-center rounded-xl", styles.iconWrap)}>
            <Icon className={cn("h-5 w-5", styles.icon)} />
          </div>

          <h2 className="mt-4 text-[17px] font-semibold tracking-tight text-white">
            {title}
          </h2>
          <div className="mt-1.5 text-[13px] leading-relaxed text-slate-400">
            {description}
          </div>

          <div className="mt-6 flex items-center gap-2.5">
            <button
              onClick={onClose}
              className="h-10 flex-1 rounded-xl border border-white/[0.08] bg-white/[0.02] text-[13px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white"
            >
              {cancelLabel}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={cn(
                "group relative inline-flex h-10 flex-1 items-center justify-center overflow-hidden rounded-xl px-4 text-[13px] font-semibold text-white transition-all duration-200 hover:translate-y-[-1px]",
                styles.confirm
              )}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
              />
              <span className="relative">{confirmLabel}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
